import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Minus, Plus, FileText } from "lucide-react";
import { PRODUCTS, STRIPE_PAYMENT_LINK } from "../data/mock";

export default function Product() {
  const { slug } = useParams();
  const product = PRODUCTS.find((p) => p.slug === slug) || PRODUCTS[0];
  const [mainIdx, setMainIdx] = useState(0);
  const [qty, setQty] = useState(1);
  const [tab, setTab] = useState("description");

  const handleBuy = () => {
    // Stripe Payment Link redirect
    window.location.href = STRIPE_PAYMENT_LINK;
  };

  return (
    <div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Gallery */}
          <div>
            <div className="bg-[#f4f5f7] rounded-sm aspect-square flex items-center justify-center overflow-hidden">
              <img
                src={product.images[mainIdx]}
                alt={product.name}
                className="w-full h-full object-contain p-8"
              />
            </div>
            <div className="mt-4 grid grid-cols-4 gap-3">
              {product.images.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setMainIdx(i)}
                  className={`bg-[#f4f5f7] rounded-sm aspect-square overflow-hidden border-2 transition-colors ${
                    mainIdx === i ? "border-[#29abe2]" : "border-transparent"
                  }`}
                >
                  <img src={src} alt={`thumb-${i}`} className="w-full h-full object-contain p-2" />
                </button>
              ))}
            </div>
          </div>

          {/* Info */}
          <div>
            <h1 className="text-3xl md:text-4xl font-semibold text-slate-800 leading-tight">
              {product.name}
            </h1>
            <div className="mt-4 text-3xl font-semibold text-slate-800">${product.price.toFixed(2)}</div>

            <p className="mt-6 text-sm text-slate-600 bg-slate-50 border-l-4 border-[#29abe2] p-4 leading-relaxed">
              <strong>Please note:</strong> Shipping on all orders is determined after purchase. We will contact you
              with your shipping quote. Orders over $500USD qualify for free shipping within Canada and the
              Contiguous United States.
            </p>

            <p className="mt-5 text-slate-600 leading-relaxed">
              {product.shortDesc}{" "}
              <a
                href={product.datasheet}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-[#29abe2] font-semibold hover:underline"
              >
                (FULL DATASHEET <FileText className="w-3.5 h-3.5" />)
              </a>
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <div className="flex items-center border border-slate-300 rounded">
                <button
                  onClick={() => setQty(Math.max(1, qty - 1))}
                  className="px-3 py-3 text-slate-600 hover:text-[#29abe2]"
                  aria-label="Decrease quantity"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <input
                  type="number"
                  value={qty}
                  onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                  className="w-14 text-center py-3 focus:outline-none"
                />
                <button
                  onClick={() => setQty(qty + 1)}
                  className="px-3 py-3 text-slate-600 hover:text-[#29abe2]"
                  aria-label="Increase quantity"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              <button
                onClick={handleBuy}
                className="bg-[#29abe2] hover:bg-[#1f9bd0] text-white font-semibold tracking-widest text-sm px-8 py-3.5 rounded transition-colors"
              >
                ADD TO CART
              </button>
            </div>

            <div className="mt-4 text-sm text-slate-500">
              Pay securely with Stripe – checkout redirects to a secure payment page.
            </div>

            <div className="mt-8 pt-6 border-t border-slate-200 text-sm text-slate-600 space-y-1">
              <div>
                <span className="font-semibold">Categories:</span>{" "}
                <Link to="/shop" className="text-[#29abe2] hover:underline">{product.category}</Link>
              </div>
              <div>
                <span className="font-semibold">SKU:</span> {product.sku}
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mt-16 border-t border-slate-200">
          <div className="flex gap-2 border-b border-slate-200 -mt-px">
            {[
              { id: "description", label: "Description" },
              { id: "additional", label: "Additional information" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-6 py-4 text-sm font-semibold transition-colors ${
                  tab === t.id
                    ? "text-[#29abe2] border-b-2 border-[#29abe2] -mb-px"
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="py-10">
            {tab === "description" && (
              <div>
                <h2 className="text-2xl font-semibold text-slate-800 mb-4">What you get with the TappyUSB:</h2>
                <ul className="space-y-3 text-slate-700">
                  {product.features.map((f, i) => (
                    <li key={i} className="flex gap-3">
                      <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#29abe2] flex-shrink-0" />
                      <span className="leading-relaxed">{f}</span>
                    </li>
                  ))}
                </ul>

                <h3 className="mt-10 text-xl font-semibold text-slate-800 mb-4">Product Specifications:</h3>
                <div className="overflow-hidden rounded-sm border border-slate-200">
                  <table className="w-full text-sm">
                    <tbody>
                      {product.specs.map((row, i) => (
                        <tr key={i} className={i % 2 === 0 ? "bg-slate-50" : "bg-white"}>
                          <td className="px-5 py-3 font-semibold text-slate-700 align-top w-1/3">{row[0]}</td>
                          <td className="px-5 py-3 text-slate-600">{row[1]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {tab === "additional" && (
              <div className="overflow-hidden rounded-sm border border-slate-200">
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="bg-slate-50">
                      <td className="px-5 py-3 font-semibold text-slate-700 w-1/3">Weight</td>
                      <td className="px-5 py-3 text-slate-600">0.17 kg</td>
                    </tr>
                    <tr>
                      <td className="px-5 py-3 font-semibold text-slate-700">Dimensions</td>
                      <td className="px-5 py-3 text-slate-600">12.5 × 8 × 3 cm</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
